import React, { useState } from 'react';
import { 
  GraduationCap, CheckCircle2, QrCode, CreditCard, 
  BookOpen, ArrowRight, Printer, ShieldCheck, User, ArrowLeft,
  Sparkles, Lock, FileText, Check, AlertCircle, RefreshCw
} from 'lucide-react';
import { INSTITUTION_INFO, DEMO_STUDENTS } from '../../data/portalData';

export const EnrolmentPortal = ({ setActiveView, setSelectedStudent }) => {
  const [activeTab, setActiveTab] = useState('courses'); // 'courses' | 'activation'
  const [selectedTrack, setSelectedTrack] = useState('student-dba');
  const [coursesConfirmed, setCoursesConfirmed] = useState(true);

  // Fresh scholar activation form state
  const [appNo, setAppNo] = useState('');
  const [surname, setSurname] = useState('');
  const [programmeChoice, setProgrammeChoice] = useState('dba');
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [activationSuccess, setActivationSuccess] = useState(null);

  const currentStudent = DEMO_STUDENTS.find(s => s.id === selectedTrack) || DEMO_STUDENTS[1];

  const handleLaunchLms = () => {
    if (setSelectedStudent) {
      setSelectedStudent(currentStudent.id);
    }
    setActiveView('lms-student');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleActivationSubmit = (e) => {
    e.preventDefault();
    setActivationError('');

    if (!appNo.trim() || !surname.trim()) {
      setActivationError('Please provide your official Application Number and Surname.');
      return;
    }

    setIsActivating(true);
    setTimeout(() => {
      setIsActivating(false);
      const generatedMatric = `AKSU/BS/2026/${programmeChoice.toUpperCase()}/${Math.floor(100 + Math.random() * 900)}`;
      setActivationSuccess({
        appNo: appNo.toUpperCase().trim(),
        surname: surname.trim(),
        programme: programmeChoice.toUpperCase(),
        matricNo: generatedMatric,
        clearanceStatus: 'Cleared & Fully Enrolled'
      });
      // Switch selected demo track to match
      const matchedTrack = DEMO_STUDENTS.find(s => s.degreeLevel.toLowerCase().includes(programmeChoice.toLowerCase()));
      if (matchedTrack) {
        setSelectedTrack(matchedTrack.id);
      }
    }, 600);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Navigation Breadcrumb & Quick Portal Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs font-sc">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveView('home')}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-[#7A470E] hover:text-white text-slate-700 text-xs font-bold transition-all cursor-pointer shadow-2xs"
            >
              <ArrowLeft className="w-3.5 h-3.5 text-[#7A470E] group-hover:text-white" />
              <span>Website Home</span>
            </button>
            <button
              onClick={() => setActiveView('admission-advert')}
              className="px-3 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-[#7A470E] text-xs font-bold transition-all cursor-pointer border border-[#7A470E]/30 hidden sm:inline-flex items-center space-x-1"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Admission Circular</span>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveView('lms-hub')}
              className="px-3.5 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#06182B] text-amber-300 text-xs font-bold transition-all cursor-pointer shadow-xs flex items-center space-x-1.5"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>LMS Central Hub</span>
            </button>
            <button
              onClick={handleLaunchLms}
              className="px-4 py-1.5 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white text-xs font-bold transition-all cursor-pointer shadow-xs flex items-center space-x-1.5"
            >
              <GraduationCap className="w-4 h-4" />
              <span>Launch Student LMS</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Top Banner in AKSU Navy and Gold */}
        <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-3xl p-6 sm:p-8 flex flex-wrap items-center justify-between gap-6 shadow-xl border-2 border-[#7A470E] relative overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#7A470E]/15 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center space-x-2 text-xs font-black uppercase tracking-wider text-[#B87A28] font-sc mb-1">
              <GraduationCap className="w-4 h-4" />
              <span>Official Academic Lifecycle Gate</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide">
              Scholar Registration & Matriculation Desk
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 mt-1 max-w-2xl font-light leading-relaxed">
              Dedicated self-service desk for newly admitted candidates and enrolled scholars: activate your portal clearance, register semester credit modules, and generate your official executive digital student identity card.
            </p>
          </div>

          {/* Action Tabs Switcher */}
          <div className="relative z-10 flex items-center bg-[#030F1C] p-1.5 rounded-2xl border border-slate-700/80 text-xs font-sc">
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                activeTab === 'courses'
                  ? 'bg-gradient-to-r from-[#0B2644] to-[#7A470E] text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>1. Course Registration & ID</span>
            </button>
            <button
              onClick={() => setActiveTab('activation')}
              className={`px-4 py-2 rounded-xl font-bold transition-all cursor-pointer flex items-center space-x-1.5 ${
                activeTab === 'activation'
                  ? 'bg-gradient-to-r from-[#0B2644] to-[#7A470E] text-white shadow-md'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>2. New Intake Portal Activation</span>
            </button>
          </div>
        </div>

        {/* TAB 1: COURSE REGISTRATION & DIGITAL ID CARD */}
        {activeTab === 'courses' && (
          <div className="space-y-6">
            
            {/* Degree Track Selector Pill */}
            <div className="bg-white rounded-2xl p-3 border border-slate-200 shadow-2xs flex flex-wrap items-center justify-between gap-3 text-xs font-sc">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-[#7A470E] animate-pulse" />
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                  Registered Academic Track:
                </span>
                <span className="font-extrabold text-[#0B2644]">
                  {currentStudent.degreeLevel} · {currentStudent.fullName}
                </span>
              </div>

              <div className="flex items-center space-x-1.5">
                <span className="text-slate-500 mr-1 hidden sm:inline">Preview Track:</span>
                {DEMO_STUDENTS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedTrack(s.id)}
                    className={`px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer text-xs ${
                      selectedTrack === s.id
                        ? 'bg-[#0B2644] text-white shadow-xs'
                        : 'bg-slate-100 text-slate-700 hover:bg-amber-50 hover:text-[#7A470E]'
                    }`}
                  >
                    {s.degreeLevel}: {s.fullName.split(' ')[0]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              
              {/* Left Column: Registered Semester Modules */}
              <div className="lg:col-span-7 bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b-2 border-slate-200 pb-4">
                  <div>
                    <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] block font-sc">
                      {currentStudent.department}
                    </span>
                    <h3 className="text-2xl font-black font-sc text-[#0B2644]">
                      {currentStudent.programmeName}
                    </h3>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Session: 2025/2026 Executive Semester | Matric: <strong className="font-mono text-[#7A470E] font-bold">{currentStudent.matricNo}</strong>
                    </div>
                  </div>
                  <span className="px-3 py-1 text-xs font-black rounded-xl bg-emerald-100 text-emerald-800 font-sc border border-emerald-300">
                    ✓ Enrolled
                  </span>
                </div>

                <div className="space-y-3">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-700 block font-sc">
                    Official Registered Modules & Credit Allocations:
                  </span>

                  <div className="space-y-2.5">
                    {currentStudent.modules.map((m) => (
                      <div 
                        key={m.code}
                        className="p-3.5 rounded-2xl bg-slate-50 border-2 border-slate-200 flex items-center justify-between text-xs hover:border-[#7A470E] transition-colors"
                      >
                        <div className="space-y-0.5 pr-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-bold text-[#0B2644] bg-amber-100 px-2 py-0.5 rounded-lg border border-amber-200">
                              {m.code}
                            </span>
                            <span className="font-bold text-slate-900">{m.title}</span>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            Facilitator: {m.lecturer} · {m.schedule}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-xs font-extrabold text-[#7A470E] block">{m.units} Units</span>
                          <span className="text-[10px] text-emerald-700 font-bold">Confirmed</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-amber-50/70 rounded-2xl border border-amber-200 flex items-center justify-between text-xs font-medium">
                    <span className="font-sc font-bold text-[#0B2644]">Total Registered Semester Credit Load:</span>
                    <strong className="text-base font-black font-mono text-[#7A470E]">
                      {currentStudent.modules.reduce((acc, curr) => acc + curr.units, 0)} Credit Units
                    </strong>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-slate-600 flex items-center space-x-1.5">
                    <CheckCircle2 className="w-4 h-4 text-[#7A470E] shrink-0" />
                    <span>Admissions clearance & Senate course confirmation verified</span>
                  </div>

                  <button
                    onClick={handleLaunchLms}
                    className="px-6 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold rounded-xl text-xs sm:text-sm shadow-md transition-all flex items-center space-x-2 cursor-pointer font-sc hover:scale-[1.02]"
                  >
                    <span>Proceed to Student LMS</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Right Column: Official Executive Student Identity Card */}
              <div className="lg:col-span-5 space-y-4">
                <div className="text-xs font-black uppercase tracking-wider text-[#7A470E] text-center font-sc">
                  Official Executive Student Identity Pass
                </div>

                {/* ID Card Front */}
                <div className="bg-gradient-to-br from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-3xl p-6 border-4 border-[#7A470E] shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#7A470E]/20 rounded-full blur-xl pointer-events-none" />

                  {/* ID Card Header */}
                  <div className="flex items-center justify-between border-b-2 border-[#7A470E] pb-3">
                    <div className="flex items-center space-x-2.5">
                      <img src="/Logo.png" alt="AKSU Logo" className="w-12 h-12 object-contain logo-glow select-none" />
                      <div>
                        <div className="text-[10px] uppercase font-black tracking-wider text-[#B87A28] font-sc">
                          Akwa Ibom State University
                        </div>
                        <div className="text-sm font-black font-sc text-white">
                          BUSINESS SCHOOL
                        </div>
                      </div>
                    </div>
                    <span className="text-[9px] font-black bg-[#7A470E] text-white px-2 py-0.5 rounded font-sc shadow">
                      STUDENT ID
                    </span>
                  </div>

                  {/* ID Card Body */}
                  <div className="py-4 flex items-center space-x-4">
                    <img 
                      src={currentStudent.avatar} 
                      alt={currentStudent.fullName} 
                      className="w-20 h-24 rounded-xl object-cover border-2 border-[#7A470E] shadow-md shrink-0"
                    />

                    <div className="space-y-1 text-xs">
                      <div className="font-sc font-black text-base text-white leading-tight">
                        {currentStudent.title && !currentStudent.title.toLowerCase().includes('executive') ? `${currentStudent.title} ` : ''}{currentStudent.fullName}
                      </div>
                      <div className="text-[11px] text-[#B87A28] font-bold font-sc">
                        {currentStudent.degreeLevel} Scholar
                      </div>
                      <div className="text-[10px] text-slate-300 line-clamp-1">
                        {currentStudent.programmeName}
                      </div>
                      <div className="pt-1 text-[11px] font-mono">
                        <span className="text-amber-400 font-bold">MATRIC: </span>
                        <span className="text-white font-bold">{currentStudent.matricNo}</span>
                      </div>
                    </div>
                  </div>

                  {/* ID Card Footer */}
                  <div className="border-t border-[#7A470E]/40 pt-2.5 flex items-center justify-between text-[9px] text-slate-300">
                    <div>
                      <span className="block font-bold text-[#B87A28] font-sc">VALID THROUGH: 2028</span>
                      <span className="text-slate-400 font-sc">KNOWLEDGE · ENTERPRISE · LEADERSHIP</span>
                    </div>
                    <QrCode className="w-8 h-8 text-white opacity-90" />
                  </div>
                </div>

                {/* Print & Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => window.print()}
                    className="flex-1 py-2.5 px-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-xl border border-slate-200 text-xs shadow-xs transition-colors flex items-center justify-center space-x-1.5 cursor-pointer font-sc"
                  >
                    <Printer className="w-3.5 h-3.5 text-[#0B2644]" />
                    <span>Print Student ID Pass</span>
                  </button>
                  <button
                    onClick={handleLaunchLms}
                    className="flex-1 py-2.5 px-3 bg-[#7A470E] hover:bg-[#5C3306] text-white font-black rounded-xl text-xs shadow-sm transition-colors flex items-center justify-center space-x-1.5 cursor-pointer font-sc"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    <span>Open LMS Coursework</span>
                  </button>
                </div>

              </div>

            </div>
          </div>
        )}

        {/* TAB 2: FRESH CANDIDATE PORTAL ACTIVATION */}
        {activeTab === 'activation' && (
          <div className="max-w-3xl mx-auto bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-10 shadow-lg space-y-6">
            <div className="text-center space-y-2 border-b border-slate-100 pb-5">
              <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-[#7A470E] flex items-center justify-center mx-auto shadow-xs">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <h2 className="text-2xl font-black font-sc text-[#0B2644]">
                Fresh Admitted Scholar Portal Activation
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 max-w-lg mx-auto">
                Newly admitted candidates who have received an official Admission Offer Letter can activate their permanent matriculation record and student LMS account below.
              </p>
            </div>

            {activationSuccess ? (
              <div className="text-center py-6 space-y-5 animate-in fade-in duration-200">
                <div className="w-16 h-16 rounded-full bg-emerald-100 border-2 border-emerald-500 text-emerald-800 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <div>
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Scholar Registration Verified & Active!
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 mt-1">
                    Welcome, <strong className="text-[#7A470E]">{activationSuccess.surname}</strong>. Your matriculation record has been confirmed by the Directorate of Academic Affairs.
                  </p>
                </div>

                <div className="max-w-md mx-auto p-5 rounded-2xl bg-slate-50 border-2 border-slate-200 text-left space-y-2 text-xs font-mono">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Application No:</span>
                    <span className="text-[#0B2644] font-bold">{activationSuccess.appNo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Enrolled Programme:</span>
                    <span className="text-[#7A470E] font-bold">{activationSuccess.programme}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Official Matric No:</span>
                    <span className="text-emerald-700 font-bold">{activationSuccess.matricNo}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Clearance Status:</span>
                    <span className="text-emerald-700 font-bold">✓ {activationSuccess.clearanceStatus}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                  <button
                    onClick={() => setActiveTab('courses')}
                    className="py-3 px-6 rounded-xl bg-slate-100 hover:bg-slate-200 text-[#0B2644] font-sc font-bold text-xs transition-colors cursor-pointer"
                  >
                    View Registered Courses & ID Pass
                  </button>
                  <button
                    onClick={handleLaunchLms}
                    className="py-3 px-6 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-sc font-bold text-xs transition-colors cursor-pointer shadow-md flex items-center justify-center space-x-2"
                  >
                    <span>Proceed to Student LMS</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleActivationSubmit} className="space-y-4">
                {activationError && (
                  <div className="p-3.5 bg-red-50 border border-red-300 rounded-xl text-xs text-red-800 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <span>{activationError}</span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold font-sc text-slate-700 mb-1">
                      Official Application Number *
                    </label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. AKSU/BS/2026/0148"
                      value={appNo}
                      onChange={(e) => setAppNo(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#7A470E]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold font-sc text-slate-700 mb-1">
                      Candidate Surname (as registered) *
                    </label>
                    <input 
                      type="text"
                      required
                      placeholder="e.g. Udoh"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#7A470E]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold font-sc text-slate-700 mb-1">
                    Admitted Degree Programme *
                  </label>
                  <select
                    value={programmeChoice}
                    onChange={(e) => setProgrammeChoice(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-bold text-slate-900 font-sc focus:outline-none focus:ring-2 focus:ring-[#7A470E]"
                  >
                    <option value="dba">Doctor of Business Administration (DBA)</option>
                    <option value="phd">Doctor of Philosophy in Business Admin (PhD BA)</option>
                    <option value="mba">Master of Business Administration (Executive MBA)</option>
                    <option value="pgd">Postgraduate Diploma in Business Admin (PGD)</option>
                    <option value="dpa">Doctor of Public Administration (DPA)</option>
                  </select>
                </div>

                <div className="p-3.5 bg-blue-50/70 rounded-xl border border-blue-200 text-[11px] text-blue-900 leading-relaxed">
                  <strong>Verification Note:</strong> Your credentials will be verified against the Senate Admission Registry. Once cleared, your semester courses will be instantly confirmed and your digital identity badge generated.
                </div>

                <button
                  type="submit"
                  disabled={isActivating}
                  className="w-full py-3 rounded-xl bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold text-xs font-sc uppercase tracking-wider transition-all shadow-md flex items-center justify-center space-x-2 cursor-pointer disabled:opacity-50"
                >
                  {isActivating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Verifying Senate Registry...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Verify & Complete Registration</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        )}

      </div>
    </div>
  );
};
